import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppInfoService } from 'core/app-info/app-info.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { DataLayerService } from 'core/data-layer-service/data-layer.service';
import { ProductAdditionalInfo, ProductDetails, ProductSpecifications, ProductSpecificationsImage, ProductWarehouse, ProductWarrantyInfo }
  from '../entities/product-entity';
import { ProductsService } from '../services/products-service/products.service';
import { ProductImagesComponent } from '../product-images/product-images.component';

@Component({
  selector: 'app-product-details',
  templateUrl: './product-details.component.html',
  styleUrls: ['./product-details.component.scss'],
  standalone: false
})
export class ProductDetailsComponent implements OnInit, OnDestroy {
  htmlSnippet: string;
  appStrings: AppLocalization;
  htmlSnippetFirst: string;
  htmlSnippetSecond: string;
  product: ProductDetails;
  features = [];
  buttonClicked = false;
  noImage;
  isLoading: boolean;
  numOfChars = 200;
  productPrice: string;
  currencySymbol: string;
  numberProps = ['listPrice', 'weight', 'minimumQuantity'];
  productInfo = { description: '', video: '' };
  productWarehouses = [];
  generalInfoOpen = false;
  additionalInfoOpen = false;
  hasAdditionalInfo = true;
  availabilityOpen = false;
  productNumber: string;
  catalogCode: string;
  catalogLogo: string;
  catalogName: string;
  warrantyOpen = false;
  warrantySpecs = {};
  availabilityIsLoading: boolean;
  additionalInfoIsLoading: boolean;
  specificationsOpen = false;
  hasSpecifications = true;
  productSpecifications = [];
  videoSource: string;
  carouselImageIndex: number;
  images = [];
  displayZoomBtn: boolean;
  displaySpecZoomBtn: boolean;
  specList = [];
  displayedColumns: string[] = ['specName', 'value'];
  specificationImage: string;
  private subs = new Subscription();

  constructor(
    private productsService: ProductsService,
    private route: ActivatedRoute,
    private appInfoService: AppInfoService,
    private dialog: MatDialog,
    private dataLayerService: DataLayerService,
  ) { }

  get isContentVisible(): boolean {
    return !this.isLoading || Boolean(this.product);
  }

  ngOnInit() {
    this.appStrings = this.appInfoService.appStrings;
    this.currencySymbol = this.appInfoService.currencySymbol;
    this.noImage = this.productsService.noImage;
    this.subs.add(this.route.params.subscribe(({ catalogCode, partNumber }: { catalogCode: string, partNumber: string }) => {
      this.isLoading = true;
      this.catalogCode = catalogCode;
      this.catalogLogo = this.productsService.catalogs.find(c => c.catalogCode === catalogCode)?.logo;
      this.catalogName = this.productsService.catalogs.find(c => c.catalogCode === catalogCode)?.name ?? '';
      this.productsService.getProductsDetails(catalogCode, partNumber).subscribe({
        next: (productDetails: ProductDetails) => this.handleProductDetails(productDetails),
        error: () => this.isLoading = false
      });
    }));
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  /**
   * Handles product details
   * @param productDetails product details
   */
  handleProductDetails(productDetails: ProductDetails) {
    this.product = {
      ...productDetails,
    };

    Object.keys(productDetails).forEach(prop => {
      if (this.numberProps.indexOf(prop) === -1 && !this.product[prop] && this.product[prop] !== false) {
        this.product[prop] = this.appStrings.notApplicable;
      }
    });

    if (this.product.listPrice) {
      this.productPrice = this.product.listPrice.toFixed(2);
    }

    this.isLoading = false;

    if (this.product && this.product.description) {
      this.htmlSnippet = this.product.description;
      this.htmlSnippetFirst = this.product.description.slice(0, this.numOfChars);
      if (this.htmlSnippet > this.htmlSnippetFirst) {
        this.htmlSnippetFirst += '...';
      }
    }

    if (!this.product.imageList || !this.product.imageList.length) {
      this.images = ['/assets/images/imageFallback.jpg'];
      this.displayZoomBtn = false;
    } else {
      this.images = this.product.imageList;
      this.displayZoomBtn = true;
    }

    this.additionalInfoOpen = false;
    this.availabilityOpen = false;
    this.warrantyOpen = false;
    this.specificationsOpen = false;

    if (this.product.breadcrumb.length > 0) {
      this.dataLayerService.logEvent('product-pageview', {
        page_type: 'product',
        product_id: this.product.breadcrumb[0].id,
        product_name: this.product.breadcrumb[0].name,
        product_sku: this.productNumber,
      });
    }
  }

  displayGeneralInfo() {
    this.generalInfoOpen = !this.generalInfoOpen;
  }

  /**
   * Get and display data for Additional Info section
   */
  displayAdditionalInfo() {
    this.additionalInfoOpen = !this.additionalInfoOpen;
    if (this.additionalInfoOpen) {
      this.additionalInfoIsLoading = true;
      this.productsService.getProductAdditionalInfo(
        this.catalogCode,
        this.product.productNumber
      ).subscribe((productInfo: ProductAdditionalInfo) => {
        this.additionalInfoIsLoading = false;
        this.productInfo = {
          ...productInfo,
          description: productInfo.description ?? this.appStrings.notApplicable
        };
        this.videoSource = this.productInfo.video;
        this.hasAdditionalInfo = !!productInfo.description || !!productInfo.video;
      });
    }
  }

  /**
   * Get and display data for Availability section
   */
  displayAvailabilityInfo() {
    this.availabilityOpen = !this.availabilityOpen;
    if (this.availabilityOpen) {
      this.availabilityIsLoading = true;
      this.productsService.getProductAvailability(this.product.productNumber)
        .subscribe((warehousesList: ProductWarehouse[]) => {
          this.availabilityIsLoading = false;
          this.productWarehouses = [
            ...warehousesList
          ];
        });
    }
  }

  /**
   * Get and display data for Warranty section
   */
  displayWarrantyInfo() {
    this.warrantyOpen = !this.warrantyOpen;
    if (this.warrantyOpen && this.product.warrantyCode !== this.appStrings.notApplicable) {
      this.productsService.getProductWarranty(
        this.catalogCode,
        this.product.warrantyCode
      ).subscribe((productWarrantyInfo: ProductWarrantyInfo) =>
        this.warrantySpecs = {
          ...productWarrantyInfo
        });
    } else {
      this.warrantySpecs = {
        description: this.appStrings.notApplicable
      };
    }
  }

  /**
   * Get and display data and image for Specification section
   */
  displaySpecifications() {
    this.specificationsOpen = !this.specificationsOpen;
    if (this.specificationsOpen) {
      this.productsService.getProductSpecifications(this.catalogCode, this.product.productNumber).subscribe(
        (productSpecificationsInfo: ProductSpecifications[]) => {
          this.specList = productSpecificationsInfo;
          this.onSpecificationChange();
        });
      this.productsService.getProductSpecificationsImage(this.catalogCode, this.product.productNumber).subscribe(
        (image: ProductSpecificationsImage) => {
          this.specificationImage = image.specificationImage;
          if (!this.specificationImage) {
            this.displaySpecZoomBtn = false;
          } else {
            this.displaySpecZoomBtn = true;
          }
          this.onSpecificationChange();
        });
    }
  }

  /**
   * Display video in fullscreen mode
   */
  toggleFullscreen() {
    const elem = document.querySelector('video');
    if (!document.fullscreenElement) {
      elem.requestFullscreen();
    }
  }

  /**
   * Display image pop-up
   */
  openImage(event: Event, hideCarousel: boolean) {
    event.stopPropagation();
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = { items: [this.specificationImage], hideCarousel };
    const dialogRef = this.dialog.open(ProductImagesComponent, dialogConfig);
    dialogRef.afterClosed();
  }

  /**
   * Display multiple image pop-up
   */
  openCarousel(event: Event, hideCarousel: boolean) {
    event.stopPropagation();
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      items: this.images,
      hideCarousel,
      index: this.carouselImageIndex,
    };
    dialogConfig.height = '80%';
    dialogConfig.width = '80%';
    const dialogRef = this.dialog.open(ProductImagesComponent, dialogConfig);
    dialogRef.afterClosed();
  }

  /**
   * Get the image index on image change
   * @param value - Image index
   */
  onImageChange(value) {
    this.carouselImageIndex = value;
  }

  private onSpecificationChange() {
    this.hasSpecifications = this.specList.length > 0 || !!this.specificationImage;
  }
}
